import React, { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Users, RefreshCw, ArrowRight, Clock, AlertTriangle,
  Building2, Calendar, User, Search, ChevronLeft, ChevronRight,
  TrendingUp, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const VDNBManagement = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [reps, setReps] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Reassignment dialog
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedRep, setSelectedRep] = useState('');
  const [reassigning, setReassigning] = useState(false);
  
  // Reset clock dialog
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetReason, setResetReason] = useState('');
  
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'Admin';
  const isStrategyAdmin = user?.role === 'strategy_support_admin';
  const canReassign = isSuperAdmin || user?.role === 'city_admin';

  useEffect(() => {
    fetchData();
    fetchReps();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, custRes] = await Promise.all([
        api.getVDNBDashboard(),
        api.getVDNBCustomers({ skip: page * 20, limit: 20 })
      ]);
      setDashboard(dashRes.data);
      setCustomers(custRes.data?.customers || []);
      setTotal(custRes.data?.total || 0);
    } catch (error) {
      console.error('Failed to load VDNB data:', error);
      toast.error('Failed to load VDNB data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReps = async () => {
    try {
      const response = await api.getUsers({ role: 'Rep', is_active: true });
      setReps(response.data?.users || []);
    } catch (error) {
      console.error('Failed to load reps:', error);
    }
  };

  const handleReassign = async () => {
    if (!selectedRep) {
      toast.error('Please select a rep');
      return;
    }
    
    setReassigning(true);
    try {
      await api.reassignVDNBCustomer(selectedCustomer.customer_id, selectedRep);
      toast.success('Customer reassigned successfully');
      setShowReassignDialog(false);
      setSelectedCustomer(null);
      setSelectedRep('');
      fetchData();
    } catch (error) {
      console.error('Reassignment failed:', error);
      toast.error(error.response?.data?.detail || 'Reassignment failed');
    } finally {
      setReassigning(false);
    }
  };

  const handleResetClock = async () => {
    if (!resetReason || resetReason.length < 5) {
      toast.error('Please provide a reason (min 5 characters)');
      return;
    }
    
    try {
      await api.resetOwnershipClock(selectedCustomer.customer_id, resetReason);
      toast.success('Ownership clock reset successfully');
      setShowResetDialog(false);
      setSelectedCustomer(null);
      setResetReason('');
      fetchData();
    } catch (error) {
      console.error('Reset failed:', error);
      toast.error(error.response?.data?.detail || 'Reset failed');
    }
  };

  const triggerNightlyJob = async () => {
    try {
      toast.info('Running VDNB job... This may take a few seconds.');
      const result = await api.triggerVDNBJob();
      const data = result.data;
      
      // Show detailed result
      let message = `VDNB job completed. Scanned: ${data.customers_scanned}, Moved: ${data.customers_moved}`;
      if (data.customers_moved > 0) {
        const reasons = data.by_reason || {};
        const breakdown = Object.entries(reasons)
          .filter(([_, count]) => count > 0)
          .map(([reason, count]) => `${reason}: ${count}`)
          .join(', ');
        if (breakdown) {
          message += ` (${breakdown})`;
        }
      }
      
      toast.success(message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger VDNB job');
    }
  };

  const MetricCard = ({ icon: Icon, iconColor, bgColor, label, value, subValue }) => (
    <Card className={`p-4 ${bgColor} rounded-2xl`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-xl ${iconColor}`}>
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <p className="text-2xl font-black text-[#111827]">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {subValue && <p className="text-[10px] text-slate-400">{subValue}</p>}
        </div>
      </div>
    </Card>
  );

  const getReasonBadge = (reason) => {
    const colors = {
      '60D': 'bg-orange-100 text-orange-700',
      '60 Days Expired': 'bg-orange-100 text-orange-700',
      '15D_INACTIVITY': 'bg-amber-100 text-amber-700',
      'CA Inactive 10D': 'bg-amber-100 text-amber-700',
      'REP_INACTIVE': 'bg-red-100 text-red-700',
      'Inactive Rep': 'bg-red-100 text-red-700'
    };
    const labels = {
      '60D': '60 Days',
      '60 Days Expired': '60 Days Expired',
      '15D_INACTIVITY': '15D Inactive',
      'CA Inactive 10D': 'CA Inactive 10D',
      'REP_INACTIVE': 'Rep Inactive',
      'Inactive Rep': 'Inactive Rep'
    };
    return (
      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${colors[reason] || 'bg-slate-100 text-slate-600'}`}>
        {labels[reason] || reason || 'Unknown'}
      </span>
    );
  };

  if (loading && !dashboard) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <RefreshCw size={24} className="animate-spin text-[#5B3DF5]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 md:px-8 md:py-4 shadow-sm sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-xl font-bold text-[#111827]">VDNB Management</h1>
            <p className="text-xs text-slate-400">Visit Done No Booking - Customer Pool</p>
          </div>
          
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={triggerNightlyJob}
                className="h-9 rounded-xl text-xs"
              >
                <RotateCcw size={14} className="mr-1" />
                Run VDNB Job
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="h-9 rounded-xl"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-[1400px] mx-auto space-y-6">
          
          {/* Dashboard Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard
              icon={Users}
              iconColor="bg-[#5B3DF5]"
              bgColor="bg-white shadow-sm"
              label="Total VDNB"
              value={dashboard?.total_vdnb || 0}
            />
            <MetricCard
              icon={TrendingUp}
              iconColor="bg-amber-500"
              bgColor="bg-white shadow-sm"
              label="Added This Week"
              value={dashboard?.added_this_week || 0}
            />
            <MetricCard
              icon={ArrowRight}
              iconColor="bg-emerald-500"
              bgColor="bg-white shadow-sm"
              label="Reassigned This Week"
              value={dashboard?.reassigned_this_week || 0}
            />
            <MetricCard
              icon={Building2}
              iconColor="bg-blue-500"
              bgColor="bg-white shadow-sm"
              label="VDNB → Booking %"
              value={`${dashboard?.vdnb_to_booking_rate || 0}%`}
            />
            <MetricCard
              icon={Clock}
              iconColor="bg-purple-500"
              bgColor="bg-white shadow-sm"
              label="Avg Days in VDNB"
              value={dashboard?.avg_days_in_vdnb || 0}
            />
          </div>

          {/* City Distribution */}
          {dashboard?.city_distribution && Object.keys(dashboard.city_distribution).length > 0 && (
            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <h3 className="font-bold text-[#111827] mb-3">City-wise Distribution</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(dashboard.city_distribution).map(([city, count]) => (
                  <div key={city} className="px-3 py-2 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-700">{city}:</span>
                    <span className="ml-2 text-sm font-bold text-[#111827]">{count}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Reason Distribution */}
          {dashboard?.reason_distribution && Object.keys(dashboard.reason_distribution).length > 0 && (
            <Card className="p-4 bg-white shadow-sm rounded-2xl">
              <h3 className="font-bold text-[#111827] mb-3">Reason Breakdown</h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(dashboard.reason_distribution).map(([reason, count]) => (
                  <div key={reason} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                    {getReasonBadge(reason)}
                    <span className="text-sm font-bold text-[#111827]">{count}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Last Job Run Info */}
          {dashboard?.last_job_run && (
            <Card className="p-4 bg-blue-50 border border-blue-100 rounded-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock size={18} className="text-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Last VDNB Job Run</p>
                    <p className="text-xs text-blue-600">
                      {dashboard.last_job_run.completed_at 
                        ? new Date(dashboard.last_job_run.completed_at).toLocaleString('en-IN')
                        : 'Never'}
                    </p>
                  </div>
                </div>
                {dashboard.last_job_run.customers_moved !== undefined && (
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-900">{dashboard.last_job_run.customers_moved}</p>
                    <p className="text-xs text-blue-600">customers moved</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* VDNB Customers Table */}
          <Card className="bg-white shadow-sm rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-[#111827]">VDNB Customers ({total})</h3>
              <div className="relative w-64">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search customers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9 rounded-xl text-sm"
                />
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Customer</th>
                    <th className="px-4 py-3 text-left font-medium">City</th>
                    <th className="px-4 py-3 text-left font-medium">First Visit</th>
                    <th className="px-4 py-3 text-left font-medium">Days Since</th>
                    <th className="px-4 py-3 text-left font-medium">Previous Rep</th>
                    <th className="px-4 py-3 text-left font-medium">Reason</th>
                    <th className="px-4 py-3 text-left font-medium">Cycles</th>
                    <th className="px-4 py-3 text-left font-medium">AV Potential</th>
                    {canReassign && <th className="px-4 py-3 text-left font-medium">Action</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {customers.filter(c => 
                    !searchQuery || 
                    c.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    c.customer_email?.toLowerCase().includes(searchQuery.toLowerCase())
                  ).map((customer) => (
                    <tr key={customer.customer_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-sm text-[#111827]">{customer.customer_name}</p>
                          <p className="text-xs text-slate-400">{customer.customer_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{customer.city || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {customer.first_visit_date ? new Date(customer.first_visit_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-medium ${
                          customer.days_since_first_visit > 60 ? 'text-[#5B3DF5]' :
                          customer.days_since_first_visit > 30 ? 'text-amber-600' : 'text-slate-600'
                        }`}>
                          {customer.days_since_first_visit || '-'} days
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{customer.previous_rep_name || '-'}</td>
                      <td className="px-4 py-3">{getReasonBadge(customer.moved_to_vdnb_reason)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-600">{customer.ownership_cycle_count || 0}</td>
                      <td className="px-4 py-3 text-sm font-medium text-emerald-600">
                        {customer.av_potential_lacs ? `₹${customer.av_potential_lacs}L` : '-'}
                      </td>
                      {canReassign && (
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedCustomer(customer);
                                setShowReassignDialog(true);
                              }}
                              className="h-7 text-xs rounded-lg"
                            >
                              Reassign
                            </Button>
                            {isSuperAdmin && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedCustomer(customer);
                                  setShowResetDialog(true);
                                }}
                                className="h-7 text-xs rounded-lg text-purple-600"
                                title="Reset 60-day clock"
                              >
                                <Clock size={12} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className="p-4 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Showing {page * 20 + 1}-{Math.min((page + 1) * 20, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="h-8 rounded-lg"
                >
                  <ChevronLeft size={14} />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * 20 >= total}
                  className="h-8 rounded-lg"
                >
                  <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Reassign Dialog */}
      <Dialog open={showReassignDialog} onOpenChange={setShowReassignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reassign Customer</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="font-medium text-[#111827]">{selectedCustomer?.customer_name}</p>
              <p className="text-xs text-slate-500">{selectedCustomer?.customer_email}</p>
            </div>
            
            <div>
              <label className="text-xs font-semibold text-slate-600">Select New Rep</label>
              <Select value={selectedRep} onValueChange={setSelectedRep}>
                <SelectTrigger className="mt-1 h-11 rounded-xl">
                  <SelectValue placeholder="Select a rep" />
                </SelectTrigger>
                <SelectContent>
                  {reps.map((rep) => (
                    <SelectItem key={rep.user_id} value={rep.user_id}>
                      {rep.rep_name || rep.email} ({rep.city})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="p-2 bg-amber-50 rounded-lg flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-500 mt-0.5" />
              <p className="text-xs text-amber-700">
                Note: 15-day inactivity clock will be reset. 60-day ownership clock will NOT be reset.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassignDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleReassign} 
              disabled={reassigning || !selectedRep}
              className="bg-[#5B3DF5] hover:bg-[#4A2FE0]"
            >
              {reassigning ? 'Reassigning...' : 'Reassign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Clock Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Ownership Clock</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-xl">
              <p className="font-medium text-[#111827]">{selectedCustomer?.customer_name}</p>
              <p className="text-xs text-slate-500">This will reset the 60-day ownership clock</p>
            </div>
            
            <div>
              <label className="text-xs font-semibold text-slate-600">Reason for Reset *</label>
              <Input
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder="Enter reason (min 5 characters)..."
                className="mt-1 h-11 rounded-xl"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleResetClock}
              disabled={!resetReason || resetReason.length < 5}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Reset Clock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VDNBManagement;
